/**
 * Real-user Core Web Vitals beacon for naturalshilajit.com.
 *
 * Answers the question the 2026-04-19 site-perf deep-dive left open
 * (Lighthouse lab LCP over-inflates 7-13x vs real devices; CrUX
 * origin-aggregate hides PDP specifically) with real customer measurements,
 * per that report's Appendix A spec. Posts to POST /api/rum/web-vitals
 * (ops/control_center/api_server.py, cvr task, 2026-08-08).
 *
 * NOT YET INSTALLED on the live theme -- this file is a deliverable for
 * whoever owns the theme/Shopify publish step (see the accompanying
 * web_vitals_theme_liquid_snippet.md for the exact Liquid injection and
 * install instructions). This session does not have the live theme checked
 * out (it lives in a separate worktree mirror per the `cro`/`shopify` track's
 * own tooling) and does not push to the live storefront without a separate,
 * explicit go, per this task's own working contract.
 *
 * Library: web-vitals v4+ (attribution build). Loaded from CDN below for a
 * fast pilot; SELF-HOST as a versioned theme asset before wide rollout to
 * avoid a third-party blocking hop (April spec's explicit recommendation) --
 * download https://unpkg.com/web-vitals@4/dist/web-vitals.attribution.iife.js
 * yourself (do not trust a model-summarized fetch of a minified JS file for
 * this -- verify the file hash against the published npm package) and swap
 * the <script src> below for the self-hosted theme asset URL.
 *
 * Guardrails (hard requirements, do not remove):
 *   - Consent-gated: skip entirely if Shopify customer privacy says no.
 *   - Never blocks first paint: load async, run after everything else.
 *   - Never throws to the page: every path is try/catch.
 *   - sendBeacon only (fires on page hide, never delays navigation).
 *   - No PII in the payload. session_id is a random client-side UUID, not
 *     tied to any customer identity.
 */
(function () {
  "use strict";

  // ABSOLUTE URL, not a relative path: this script runs on naturalshilajit.com
  // (Shopify's own servers), which does not know this route -- a relative
  // "/api/rum/web-vitals" resolves against Shopify and 404s there every time.
  // The real backend (ops/control_center/api_server.py) is only reachable at
  // its own public host. Mirrors the proven working pattern in
  // ops/ads_intel/deploy_atc_shadow_beacon.py's ENDPOINT constant (verified
  // live 2026-08-09 after this exact relative-path bug was caught via a real
  // browser network-tab check on the dev preview theme: the beacon fired but
  // 404'd against Shopify until switched to this absolute URL).
  var ENDPOINT = "https://api.shilajit.work/api/rum/web-vitals";
  var SAMPLE_RATE = 1.0; // 100% for the first 14 days per the April spec; drop to 0.1 after
  var SESSION_KEY = "ns_rum_session_id";

  function consentAllowed() {
    // Codex round 1 MEDIUM #3: fail CLOSED, not open. A missing/erroring
    // consent API must never be read as "consent granted" -- this script
    // runs at window.load, by which point Shopify's Customer Privacy API
    // (window.Shopify.customerPrivacy) should be initialized if the shop
    // has consent management configured at all; if it genuinely is not
    // present on this shop, the safe assumption is still "do not send,"
    // not "send by default."
    //
    // Uses analyticsProcessingAllowed() in preference to
    // saleOfDataAllowed(): this is first-party performance telemetry sent
    // only to our own server, never shared with a third party, so the
    // "analytics" consent category is the semantically correct gate, not
    // the "sale of data" one. VERIFY the exact global
    // (window.Shopify.customerPrivacy vs window.shopifyCustomerPrivacy)
    // and method names against the live theme's actual pixel-manager setup
    // before install -- this session could not check that live.
    try {
      var api = (window.Shopify && window.Shopify.customerPrivacy) || window.shopifyCustomerPrivacy;
      if (!api) return false;
      if (typeof api.analyticsProcessingAllowed === "function") {
        return api.analyticsProcessingAllowed() === true;
      }
      if (typeof api.saleOfDataAllowed === "function") {
        return api.saleOfDataAllowed() === true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function sessionId() {
    try {
      var existing = sessionStorage.getItem(SESSION_KEY);
      if (existing) return existing;
      var id = "s_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SESSION_KEY, id);
      return id;
    } catch (e) {
      return null; // sessionStorage blocked (private mode etc) -- fine, just no stitching
    }
  }

  function deviceType() {
    try {
      if (navigator.userAgentData && typeof navigator.userAgentData.mobile === "boolean") {
        return navigator.userAgentData.mobile ? "mobile" : "desktop";
      }
      var ua = navigator.userAgent || "";
      if (/Tablet|iPad/i.test(ua)) return "tablet";
      if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
      return "desktop";
    } catch (e) {
      return "other";
    }
  }

  function templateFromLiquid() {
    // Set by the Liquid injection below (window.__nsTemplate). Falls back to
    // "other" so a missing/misconfigured injection degrades gracefully
    // instead of breaking the beacon.
    var t = window.__nsTemplate;
    var allowed = ["product", "collection", "home", "blog", "article"];
    return allowed.indexOf(t) !== -1 ? t : "other";
  }

  function connectionType() {
    try {
      return (navigator.connection && navigator.connection.effectiveType) || null;
    } catch (e) {
      return null;
    }
  }

  function sendMetric(metric) {
    if (!consentAllowed()) return;
    try {
      var payload = {
        metric: metric.name,
        value: metric.value,
        rating: metric.rating,
        attribution: metric.attribution || null,
        template: templateFromLiquid(),
        page_path: location.pathname,
        device: deviceType(),
        connection: connectionType(),
        session_id: sessionId(),
        build_id: window.__nsThemeBuildId || null,
        ts: Date.now(),
      };
      var body = JSON.stringify(payload);
      // text/plain, not application/json: this is a cross-origin POST
      // (naturalshilajit.com -> api.shilajit.work) and the backend's
      // CORSMiddleware allow-list does not include the storefront origin
      // (only the internal dashboard does -- see api_server.py). application/json
      // is not a CORS-safelisted content type, so it would need a preflight
      // that has nowhere to get permission from, and the browser would never
      // send the real request at all. text/plain keeps this a CORS "simple
      // request" (no preflight -- always sent), same as the proven
      // ops/ads_intel/deploy_atc_shadow_beacon.py TRACKER_JS pattern. The
      // FastAPI route parses the body as JSON regardless of Content-Type
      // (request.json() with a raw-decode fallback), so the payload itself
      // is unaffected.
      if (navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "text/plain;charset=UTF-8" }));
      } else {
        // Old-browser fallback; keepalive lets it survive page unload same as sendBeacon.
        fetch(ENDPOINT, { method: "POST", body: body, headers: { "Content-Type": "text/plain;charset=UTF-8" }, keepalive: true }).catch(function () {});
      }
    } catch (e) {
      // Never let RUM break the page (April spec's hard guardrail) -- but
      // Codex round 1 MEDIUM #4 correctly flagged that fully silent
      // failures leave zero observability if this ever breaks in
      // production. console.warn (not .error, so it doesn't trip
      // Lighthouse's "Browser errors were logged to console" audit) costs
      // nothing and never surfaces to the customer, but gives anyone in
      // DevTools -- or a future automated console-scrape check -- a
      // breadcrumb instead of silent, unexplained zero samples.
      try { console.warn("[ns-rum] sendMetric failed", e); } catch (e2) {}
    }
  }

  function loadWebVitalsAndInit() {
    try {
      if (Math.random() > SAMPLE_RATE) return;
      var script = document.createElement("script");
      script.src = "https://unpkg.com/web-vitals@4/dist/web-vitals.attribution.iife.js";
      script.async = true;
      script.onload = function () {
        try {
          var wv = window.webVitals;
          if (!wv) return;
          wv.onLCP(sendMetric, { reportAllChanges: false });
          wv.onINP(sendMetric, { reportAllChanges: false });
          wv.onCLS(sendMetric, { reportAllChanges: false });
          wv.onTTFB(sendMetric);
          wv.onFCP(sendMetric);
        } catch (e) {
          try { console.warn("[ns-rum] web-vitals init failed", e); } catch (e2) {}
        }
      };
      script.onerror = function () {
        // Codex round 1 MEDIUM #4: a blocked/unreachable CDN silently
        // disables ALL telemetry for this pageview with no signal beyond
        // "no samples arrived," which is indistinguishable from "no
        // traffic." console.warn is a cheap breadcrumb; the real fix is
        // self-hosting the library (removes this failure mode outright,
        // see the file header) -- do that before wide rollout, not just
        // this warn. Once live traffic exists, a freshness watchdog on
        // rum_web_vitals (same DM-209 pattern as
        // ops/company_memory/website_cro_daily_healthcheck.py) would catch
        // a sustained CDN outage; not built yet since there is no live
        // data to watch until this script is actually installed.
        try { console.warn("[ns-rum] web-vitals library failed to load from CDN"); } catch (e2) {}
      };
      document.head.appendChild(script);
    } catch (e) {
      try { console.warn("[ns-rum] instrumentation init failed", e); } catch (e2) {}
    }
  }

  if (document.readyState === "complete") {
    loadWebVitalsAndInit();
  } else {
    window.addEventListener("load", loadWebVitalsAndInit);
  }
})();
